import React, { useState, useEffect, useContext } from "react";
import ModalBox from "../elements/ModalBox/ModalBox";
import Button from "../elements/Button/Button";
import Textarea from "../elements/TextArea/TextArea";
import Textinput from "../elements/TextInput/TextInput";
import { AuthContext } from "../../context/AuthContext";
import CryptoJS from "crypto-js";
import { useNavigate } from "react-router-dom";
import Notification from "../Notification/Notification";
import Swal from "sweetalert2";

const MessageTemplate = () => {
  const { token } = useContext(AuthContext);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateText, setTemplateText] = useState("");
  const [messageTemplates, setMessageTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const showNotification = (message, type) => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification({ message: "", type: "" });
    }, 3000);
  };

  const locateDashboard = () => {
    navigate("/Dashboard");
  };

  const openForm = () => {
    setIsModalOpen(true);
  };

  const closeForm = () => {
    setIsModalOpen(false);
    setTemplateName("");
    setTemplateText("");
  };

  useEffect(() => {
    messageDetails();
  }, []);

  const saveTemplateDetails = async () => {
    if (!templateName.trim()) {
      showNotification("Enter the template name", "error");
      return;
    }

    if (!templateText.trim()) {
      showNotification("Enter the template text", "error");
      return;
    }

    const encryptedMetadata = localStorage.getItem("metadata");
    let company_id = "";
    if (encryptedMetadata) {
      const decryptedMetadata = JSON.parse(
        CryptoJS.AES.decrypt(
          encryptedMetadata,
          process.env.REACT_APP_SECRETKEY
        ).toString(CryptoJS.enc.Utf8)
      );
      company_id = decryptedMetadata?.cid || "";
    }

    const serverURL = process.env.REACT_APP_SERVER_URL;
    const jsonData = {
      cid: company_id.toString(),
      title: templateName,
      template: templateText,
    };
    const encryptedData = CryptoJS.AES.encrypt(
      JSON.stringify(jsonData),
      process.env.REACT_APP_SECRETKEY
    ).toString();

    setIsLoading(true);
    try {
      const response = await fetch(
        `${serverURL}/messagetemp/insertMessageTemplate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
          body: JSON.stringify({ data: encryptedData }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        showNotification(data.error || "Something went wrong", "error");
        throw new Error(data.error);
      }
      showNotification("Template saved successfully!", "success");
      closeForm();
      messageDetails();
    } catch (error) {
      console.error("Error saving template:", error);
      showNotification("An error occurred while saving the template", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const messageDetails = async () => {
    const encryptedMetadata = localStorage.getItem("metadata");
    let company_id = "";
    if (encryptedMetadata) {
      const decryptedMetadata = JSON.parse(
        CryptoJS.AES.decrypt(
          encryptedMetadata,
          process.env.REACT_APP_SECRETKEY
        ).toString(CryptoJS.enc.Utf8)
      );
      company_id = decryptedMetadata?.cid || "";
    }

    const serverURL = process.env.REACT_APP_SERVER_URL;
    const jsonData = { cid: company_id.toString() };
    const encryptedData = CryptoJS.AES.encrypt(
      JSON.stringify(jsonData),
      process.env.REACT_APP_SECRETKEY
    ).toString();

    setIsLoading(true);
    try {
      const response = await fetch(
        `${serverURL}/messagetemp/getMessageTemplates`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            token,
          },
          body: JSON.stringify({ data: encryptedData }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        showNotification(data.error || "Something went wrong", "error");
        throw new Error(data.error);
      }
      const messageTemplates = data.data || [];
      setMessageTemplates(messageTemplates);
    } catch (error) {
      console.error("Error Fetching template:", error);
      showNotification(
        "An error occurred while fetching the template",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const updateStatus = async (templateId) => {
    if (!templateId) {
      console.log("No template ID provided. Exiting function.");
      return;
    }

    const encryptedMetadata = localStorage.getItem("metadata");
    let company_id = "";
    if (encryptedMetadata) {
      const decryptedMetadata = JSON.parse(
        CryptoJS.AES.decrypt(
          encryptedMetadata,
          process.env.REACT_APP_SECRETKEY
        ).toString(CryptoJS.enc.Utf8)
      );
      company_id = decryptedMetadata?.cid || "";
    }
    const serverURL = process.env.REACT_APP_SERVER_URL;
    const jsonData = { aid: templateId, cid: company_id.toString() };
    const encryptedData = CryptoJS.AES.encrypt(
      JSON.stringify(jsonData),
      process.env.REACT_APP_SECRETKEY
    ).toString();

    setIsLoading(true);

    try {
      const response = await fetch(`${serverURL}/messagetemp/setUpdateStatus`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token,
        },
        body: JSON.stringify({ data: encryptedData }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Something went wrong");
      }

      showNotification("Template status updated successfully", "success");
      messageDetails();
    } catch (error) {
      showNotification(
        "An error occurred while updating the template status",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleActivate = (templateId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to activate this template?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, activate it!",
    }).then((result) => {
      if (result.isConfirmed) {
        updateStatus(templateId);
      }
    });
  };

  const modal_Body = (
    <div className="h-auto">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
        <div className="space-y-4">
          <div className="w-full">
            <Textinput
              value={templateName || ""}
              onChange={(e) => setTemplateName(e.target.value)}
              label="Template Name"
              labelclass="mb-2 text-sm font-medium text-gray-700"
              span="*"
              className="w-full border-2 border-blue-100 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter Template Name"
            />
          </div>
          <div className="w-full">
            <Textarea
              value={templateText}
              onChange={(e) => setTemplateText(e.target.value)}
              label="Text"
              labelclass="mb-2 text-sm font-medium text-gray-700"
              span="*"
              className="w-full h-32 sm:h-40 lg:h-48 border-2 border-blue-100 rounded-md px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter the Text"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const Modal_Footer = (
    <div className="flex flex-col-reverse sm:flex-row justify-end items-center gap-3 px-4 sm:px-6 lg:px-8 py-4 bg-gray-50 rounded-b-2xl">
      <Button
        className="border-2 border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50 w-full sm:w-auto px-6 py-2 rounded-md transition-colors duration-200"
        onClick={closeForm}
      >
        Cancel
      </Button>
      <Button
        className="border-2 border-green-600 bg-green-600 text-sm text-white hover:bg-green-700 w-full sm:w-auto px-6 py-2 rounded-md transition-colors duration-200"
        onClick={saveTemplateDetails}
      >
        Save Template
      </Button>
    </div>
  );

  return (
    <div className="w-full p-4 lg:p-6 bg-white border border-gray-200 rounded-lg shadow">
      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="flex flex-col items-center">
            <span className="text-white mt-4">Loading...</span>
          </div>
        </div>
      )}

      <Notification
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />

      <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
        <div className="mt-3">
          <p className="text-black text-lg font-medium">Message Template</p>
        </div>
        <div className="sm:ml-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button
            className="pt-2 pb-2 px-4 rounded-lg bg-green-700 text-white text-sm w-full sm:w-auto"
            onClick={openForm}
          >
            Add New Template
          </button>
          {isModalOpen && (
            <div>
              <ModalBox
                Modal_Body={modal_Body}
                modal_Body_Class="flex-1 overflow-y-auto"
                modal_Fooder_Class=""
                Modal_Close_Func={closeForm}
                Modal_Header="Add New Template"
                modal_Header_Text="px-4 sm:px-6 lg:px-8 py-4 text-lg font-semibold text-gray-900"
                Modal_Footer={Modal_Footer}
                modal_Header_Class="border-b border-gray-200 bg-white rounded-t-2xl"
                modal_Close_Class="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                cardClass="bg-white rounded-2xl shadow-xl w-11/12 sm:w-8/12 lg:w-6/12 xl:w-5/12 max-h-[90vh] max-w-2xl mx-4 flex flex-col"
              />
            </div>
          )}
        </div>
      </div>

      {/* Responsive table wrapper */}
      <div className="relative flex flex-col w-full h-auto text-gray-700 bg-white rounded-xl bg-clip-border">
        <div className="overflow-x-auto">
          <table className="w-full text-left table-auto min-w-[600px]">
            <thead>
              <tr className="bg-sky-50">
                <th className="p-2 lg:p-4 border-b border-blue-gray-100 bg-blue-gray-50 min-w-[150px]">
                  <p className="block font-sans text-sm lg:text-md antialiased font-normal leading-none text-black">
                    Template Name
                  </p>
                </th>
                <th className="p-2 lg:p-4 border-b border-blue-gray-100 bg-blue-gray-50 min-w-[250px]">
                  <p className="block font-sans text-sm lg:text-md antialiased font-normal leading-none text-black">
                    Text
                  </p>
                </th>
                <th className="p-2 lg:p-4 border-b border-blue-gray-100 bg-blue-gray-50 min-w-[120px]">
                  <p className="block font-sans text-sm lg:text-md antialiased font-normal leading-none text-black">
                    Actions
                  </p>
                </th>
              </tr>
            </thead>
            <tbody>
              {messageTemplates && messageTemplates.length > 0 ? (
                messageTemplates.map((template, index) => (
                  <tr
                    key={index}
                    className={`border-b border-blue-gray-50 ${
                      index % 2 === 0 ? "bg-[#F8F8F8]" : ""
                    }`}
                  >
                    <td className="p-2 lg:p-4 border-b border-blue-gray-50">
                      <p
                        className="block font-sans text-sm antialiased font-normal leading-normal text-blue-gray-900 truncate max-w-[150px] lg:max-w-[200px]"
                        title={template.title}
                      >
                        {template.title}
                      </p>
                    </td>
                    <td className="p-2 lg:p-4 border-b border-blue-gray-50">
                      <p
                        className="block font-sans text-sm antialiased font-normal leading-normal text-blue-gray-900 truncate max-w-[250px] lg:max-w-[350px]"
                        title={template.template}
                      >
                        {template.template}
                      </p>
                    </td>
                    <td className="p-2 lg:p-4 border-b border-blue-gray-50">
                      {template.status !== true ? (
                        <button
                          className="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors duration-200"
                          onClick={() => handleActivate(template.aid)}
                        >
                          Activate
                        </button>
                      ) : (
                        <span className="px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-md">
                          Active
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    className="py-8 px-4 border-b text-center text-gray-500"
                  >
                    No templates available. Create your first template to get
                    started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MessageTemplate;
